'use client';

// useKeyboardVisibility - Hook to detect virtual keyboard open/close
// Story 4.5: Mobile-Optimized Bot Interface
// Uses visualViewport API with fallback for unsupported browsers

import { useState, useEffect, useCallback } from 'react';

export interface KeyboardVisibilityState {
  /** Whether the virtual keyboard is currently open */
  isKeyboardOpen: boolean;
  /** Estimated height of the keyboard in pixels (0 if closed) */
  keyboardHeight: number;
}

// Threshold to distinguish keyboard from other viewport changes (e.g., address bar)
const KEYBOARD_HEIGHT_THRESHOLD = 100;

/**
 * useKeyboardVisibility - Detects virtual keyboard visibility on mobile devices
 *
 * Uses the visualViewport API to detect when the on-screen keyboard opens or closes.
 * Returns the current keyboard state including visibility and estimated height.
 *
 * @returns {KeyboardVisibilityState} Object with isKeyboardOpen and keyboardHeight
 *
 * @example
 * ```tsx
 * function ChatInput() {
 *   const { isKeyboardOpen, keyboardHeight } = useKeyboardVisibility();
 *
 *   useEffect(() => {
 *     if (isKeyboardOpen) {
 *       // Scroll to ensure input is visible
 *       messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
 *     }
 *   }, [isKeyboardOpen]);
 *
 *   return <input style={{ marginBottom: keyboardHeight }} />;
 * }
 * ```
 */
export function useKeyboardVisibility(): KeyboardVisibilityState {
  const [state, setState] = useState<KeyboardVisibilityState>({
    isKeyboardOpen: false,
    keyboardHeight: 0,
  });

  const handleResize = useCallback(() => {
    if (typeof window === 'undefined' || !window.visualViewport) {
      return;
    }

    const viewport = window.visualViewport;
    // Compare current viewport height to window inner height
    // When keyboard opens, viewport height decreases
    const heightDiff = window.innerHeight - viewport.height;
    const isOpen = heightDiff > KEYBOARD_HEIGHT_THRESHOLD;

    setState({
      isKeyboardOpen: isOpen,
      keyboardHeight: isOpen ? heightDiff : 0,
    });
  }, []);

  useEffect(() => {
    // SSR guard and feature detection
    if (typeof window === 'undefined' || !window.visualViewport) {
      return;
    }

    const viewport = window.visualViewport;

    // Initial check
    handleResize();

    // Listen for viewport changes
    viewport.addEventListener('resize', handleResize);
    viewport.addEventListener('scroll', handleResize);

    return () => {
      viewport.removeEventListener('resize', handleResize);
      viewport.removeEventListener('scroll', handleResize);
    };
  }, [handleResize]);

  return state;
}

export default useKeyboardVisibility;
